import Immutable from "immutable";

import _ from "lodash";
import EventEmitter from "eventemitter3";
import ErrorUtils from "../utils/ErrorUtils";
import AppConfig from "../config/app";

const MAX_ERRORS = 3;
const TIME_BEFORE_REMOVE = 120000; //2 minutes

const ERROR_CODE_DESIGN_FILE_NOT_CURRENT = 220;
const ERROR_CODE_INVALID_PARAMETER = 300;

/**
 * @typedef {import("./types.mjs").DesignData} DesignData
 *
 * @typedef {Object} SaveFunctionResult
 * @prop {string} eTag
 * @prop {boolean} saved
 * @prop {number} status
 */

/**
 * Injected sav function for design files
 * @callback SaveFunction
 * @param {string} designKey
 * @param {Object} saveData
 * @param {Object} saveData.data - Design file json
 * @param {Object} saveData.file
 * @param {boolean} saveData.file.isNew
 * @param {string} saveData.file.eTag
 * @returns {Promise<SaveFunctionResult>}
 */

class DesignFile extends EventEmitter {
  /** @type {SaveFunction=} */ saveFunc;
  /** @type {DesignData | null} */ data;

  /** @type {boolean} */ _isCleanupCandidate;
  /** @type {string | null} */ _cleanupPageId;
  /** @type {Object | null} */ _cleanupStore;

  /**
   * @param {Object} props
   * @param {DesignData} props.data
   * @param {string} props.siteId - design key
   * @param {SaveFunction=} props.saveFunc
   * @param {string | null} props.eTag
   * @param {boolean=} props.isNew
   * @param {boolean=} props.inSync
   */
  constructor({ data, siteId, saveFunc, eTag = null, isNew = false, inSync }) {
    super();

    this.data = data; //Should be an Immutable data structure
    this.siteId = siteId; // design key
    this.saveFunc = saveFunc;
    this.eTag = eTag;
    this.isNew = isNew;

    this.id = this.data.get("id");
    this.type = this.data.get("type");

    this.dataInProcess = null;

    this.inUse = true; //flag to determine if data is still in use, can use to remove design file after sync

    this.inSync = inSync ?? true;

    this.isSaving = false;

    this.errors = 0;
    this.lastSave = 0; //timestamp

    this.destroyed = false;

    this._isCleanupCandidate = false;
    this._cleanupPageId = null;
    this._cleanupStore = null;
  }

  /**
   * Updates file with new data
   * @param {DesignData} data
   */
  update(data) {
    if (!Immutable.is(this.data, data)) {
      this.data = data;
      this.inSync = false || AppConfig.demoMode;
    }
  }

  save() {
    if (this.isSaving) {
      return;
    }

    if (this.errors == MAX_ERRORS) {
      return;
    }

    if (
      !AppConfig.demoMode &&
      this.siteId != null &&
      this.data != null &&
      this.saveFunc
    ) {
      this.isSaving = true;
      this.dataInProcess = this.data;

      this.saveFunc(this.siteId, {
        data: this.dataInProcess.toJS(), //convert Immutable to JSON
        file: { isNew: this.isNew, eTag: this.eTag },
      })
        .then(this._saveSuccess.bind(this))
        .catch(this._saveFailed.bind(this));
    }
  }

  /**
   * Handles save result and emits a success or error.
   * @param {SaveFunctionResult} data
   */
  _saveSuccess(data) {
    if (Immutable.is(this.dataInProcess, this.data)) {
      this.inSync = true;
    }

    if (data.saved !== true) {
      this.isSaving = false;
      this.errors = MAX_ERRORS; //stop trying to save
      var error = {
        message: data.saved,
      };

      this.emit("error", error, this);
    } else {
      this.eTag = data.eTag;
      this.errors = 0;
      this.isNew = false; //if it was new and save was successful, it is no longer new
      this.isSaving = false;
      this.lastSave = Date.now();
      this.emit("save", this);
    }
  }

  _saveFailed(err) {
    var error = ErrorUtils.getFormattedError(err);
    if (ErrorUtils.isRecoverableStatus(err.status)) {
      if (_.has(error, "code")) {
        var code = error.code;
        switch (code) {
          case ERROR_CODE_DESIGN_FILE_NOT_CURRENT:
          case ERROR_CODE_INVALID_PARAMETER:
            this.errors = MAX_ERRORS; //stop trying to save
            break;
        }
      }
    } else {
      this.errors = MAX_ERRORS; //stop trying to save
    }

    this.errors++;
    this.isSaving = false;
    this.emit("error", error, this);
  }

  isUnableToSave() {
    return this.errors >= MAX_ERRORS;
  }

  destroy() {
    this.destroyed = true;
    this.data = null;
    this.dataInProcess = null;
  }

  /**
   * Checks file is valid for removal.
   * Checks if file changes are in sync with the design and not in use,
   * and either hasn't been changed or hasn't been updated in two minutes.
   * @returns {boolean}
   */
  canRemove() {
    return (
      !this.inUse &&
      this.inSync &&
      !this.isSaving &&
      (!this.lastSave || Date.now() - this.lastSave >= TIME_BEFORE_REMOVE)
    );
  }
}

export default DesignFile;
